import { useEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { erApiFeil, erNettfeil } from '../api/feil.ts';
import { useHypoteseGrunnlag } from '../api/useHypoteseGrunnlag.ts';
import { useHypoteseListe, useHypoteseTellinger } from '../api/useHypoteseBehandling.ts';
import { useHypoteseConfig, useHypoteser, type HypothesisConfig } from '../api/useHypotheses.ts';
import { useMe } from '../api/useMe.ts';
import { useAvlysMoete, useMoete, useMoeteListe, useMoeteSkriving, useOpprettMoete, type DecisionMeeting, type DecisionMeetingItem, type DecisionMeetingSummary } from '../api/useMoeter.ts';
import { Knapp } from '../innboks/Detalj.tsx';
import { Toast, type ToastInnhold } from '../innboks/Toast.tsx';
import { Dialog } from '../skall/Dialog.tsx';
import { useTilgang } from '../skall/tilgang.ts';
import { useSmal } from '../skall/useSmalSkjerm.ts';
import { Ikon } from '../status/Ikon.tsx';
import { hypoteseOppforing, hypoteseStatusNoekkel, tokenVar } from '../status/status.ts';
import { Beslutningslogg } from './Beslutningslogg.tsx';
import { Fot, Tekstknapp, Varsel } from './deler.tsx';
import { Skjelettrader } from './HListe.tsx';
import { nummerTekst } from './Kvittering.tsx';
import { isoDag, navnAv, statusNavn } from './lesning.ts';
import { alleRader, fristKort, innsendere, klokke, langDato, MAKS_SITATER, moeteNavn, moeteStatus, navneliste } from './moeteLesning.ts';
import { aapneSkjermen } from './moeteSynk.ts';
import { erHBehandler, erHLeserPluss, H_MOETER } from './rommet.ts';
import { kortDatoAv } from './setning.ts';

/**
 * «Møter» (H3.2, W1–W4c, W11): lista til venstre, gruppert etter tilstand, og møtet til høyre —
 * agendaen før møtet, beslutningsloggen etter. Siden er for leser og opp; en behandler lager møter
 * og endrer agendaen, den som registrerer starter og kjører møtet. Under 900 px er lista og møtet
 * to steg (DD-51).
 */
export function MoeterSide({ id }: { id: string | null }) {
  const tilgang = useTilgang();
  const smal = useSmal();
  const naviger = useNavigate();
  const { state, pathname } = useLocation();
  const liste = useMoeteListe({});
  const tellinger = useHypoteseTellinger();
  const behandler = erHBehandler(tilgang);
  const [nytt, setNytt] = useState(false);
  const [toast, setToast] = useState<ToastInnhold | null>(() => {
    const k = (state as { kvittering?: unknown } | null)?.kvittering;
    return typeof k === 'string' ? { tekst: k } : null;
  });
  useEffect(() => {
    if ((state as { kvittering?: unknown } | null)?.kvittering !== undefined) void naviger(pathname, { replace: true, state: null });
  }, [state, naviger, pathname]);

  const grupper = useMemo(() => grupperMoeter(liste.data ?? []), [liste.data]);
  const rader = grupper.flatMap((g) => g.rader);
  const foerste = rader[0];

  // Over 900 px velges første møte når lista kommer (DD-57). Under er lista det man ser først. Lista
  // står mens adressen byttes, så en knapp i den ikke forsvinner under trykket.
  const autovelg = !smal && id === null && foerste ? foerste.id : null;
  useEffect(() => {
    if (autovelg) void naviger(`/h/moter/${autovelg}`, { replace: true });
  }, [autovelg, naviger]);

  const nyttKnapp = behandler ? (
    <button type="button" className="panel-knapp-sekundaer panel-knapp-liten" onClick={() => setNytt(true)}>
      <Ikon navn="add-outline" />
      Nytt møte
    </button>
  ) : null;

  const tom = liste.isSuccess && rader.length === 0;
  const vurderes = tellinger.data?.reviewing;

  const listeFlate = (
    <section className="hb-liste" aria-label="Møter">
      <div className="hb-liste-rull">
        <div className="hb-liste-topp">
          <div className="hb-liste-tittel">
            <h1 className="panel-h1" id="moeter-h" tabIndex={-1}>
              Møter
            </h1>
            {nyttKnapp}
          </div>
        </div>
        {!behandler && tilgang.kjent ? (
          <p className="h-grunn mo-rolle" role="note">
            <Ikon navn="lock-closed-outline" className="h-grunn-ikon" />
            <span>Du har leserolle i Hypoteser og kan ikke endre møtene.</span>
          </p>
        ) : null}
        {liste.isPending ? (
          <Skjelettrader etikett="Henter møtene" />
        ) : liste.isError ? (
          <div className="hb-liste-melding">
            <Varsel tone="info" handling={<Tekstknapp onClick={() => void liste.refetch()}>Prøv igjen</Tekstknapp>}>
              Møtene kunne ikke hentes.
            </Varsel>
            <p className="hb-dempet">Innboksen og hypotesene virker som før.</p>
          </div>
        ) : (
          <ul className="hb-rader">
            {grupper.map((g) => (
              <MoeteGruppe key={g.status} navn={g.navn} rader={g.rader} valgt={smal ? null : id} />
            ))}
          </ul>
        )}
      </div>
      {smal ? null : (
        <div className="hb-liste-fot" aria-hidden="true">
          <kbd>J</kbd>
          <kbd>K</kbd> neste og forrige · <kbd>↵</kbd> åpne · <kbd>?</kbd> alle
        </div>
      )}
    </section>
  );

  const tomFlate = (
    <div className="hb-tomflate">
      <div className="hb-tom" data-testid="moeter-tom">
        <div className="hb-liste-tittel">
          <h1 className="panel-h1">Møter</h1>
          {nyttKnapp}
        </div>
        <p className="mo-tom-tittel">Ingen møter ennå.</p>
        <p className="hb-dempet">{`Et møte er en dato, en beslutningstaker og hypotesene som skal opp.${vurderes !== undefined ? ` ${vurderes} står i Vurderes og kan legges på et møte fra agendaen.` : ''}`}</p>
        {behandler ? (
          <div>
            <button type="button" className="panel-knapp" onClick={() => setNytt(true)}>
              <Ikon navn="add-outline" />
              Nytt møte
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );

  const detalj = id ? <MoeteDetalj key={id} id={id} tilbake={{ navn: 'Møter', til: H_MOETER.sti }} onKvittering={(tekst) => setToast({ tekst })} /> : <section className="hb-detalj hb-detalj-tom" aria-label="Ingen møter valgt" />;

  return (
    <>
      {tom ? tomFlate : smal ? (id ? detalj : listeFlate) : (
        <div className="hb-todelt">
          {listeFlate}
          {detalj}
        </div>
      )}
      {nytt ? (
        <NyttMoeteDialog
          onLukk={() => setNytt(false)}
          onOpprettet={(m) => {
            setNytt(false);
            void naviger(`/h/moter/${m.id}`);
          }}
        />
      ) : null}
      {toast ? <Toast innhold={toast} onLukk={() => setToast(null)} /> : null}
    </>
  );
}

const GRUPPER: Array<DecisionMeetingSummary['status']> = ['planlagt', 'venter_bekreftelse', 'bekreftet'];

export function grupperMoeter(rader: DecisionMeetingSummary[]) {
  return GRUPPER.map((status) => {
    const i = rader.filter((m) => m.status === status);
    return { status, navn: moeteStatus(status).etikett, rader: status === 'bekreftet' ? [...i].reverse() : i };
  }).filter((g) => g.rader.length > 0);
}

function MoeteGruppe({ navn, rader, valgt }: { navn: string; rader: DecisionMeetingSummary[]; valgt: string | null }) {
  return (
    <>
      <li className="hb-gruppe">{`${navn} · ${rader.length}`}</li>
      {rader.map((m) => (
        <li key={m.id}>
          <MoeteRad m={m} valgt={m.id === valgt} til={`/h/moter/${m.id}`} />
        </li>
      ))}
    </>
  );
}

/** Innboksens rad (W1): ikonet, navnet, antallet som radens tall, og én meta-linje med hvem. */
export function MoeteRad({ m, valgt, til, testid }: { m: DecisionMeetingSummary; valgt: boolean; til: string; testid?: string }) {
  const me = useMe();
  const megId = me.data?.id;
  const s = moeteStatus(m.status);
  const forMeg = m.status === 'venter_bekreftelse' && m.decider.id === megId;
  const ord = m.status === 'planlagt' ? (m.itemCount === 1 ? 'hypotese' : 'hypoteser') : m.itemCount === 1 ? 'beslutning' : 'beslutninger';
  const meta: ReactNode[] =
    m.status === 'planlagt'
      ? [`${navnAv(m.decider)} bestemmer`, m.registeredBy.id === megId ? 'du registrerer' : `${navnAv(m.registeredBy)} registrerer`]
      : m.status === 'venter_bekreftelse'
        ? [forMeg ? `registrert av ${navnAv(m.registeredBy)}` : `${navnAv(m.decider)} bestemmer`, m.sentAt ? `sendt ${kortDatoAv(m.sentAt)} ${klokke(m.sentAt)}` : null]
        : [`${navnAv(m.decider)} ${m.confirmedAt ? kortDatoAv(m.confirmedAt) : ''}`.trim()];
  return (
    <Link to={til} className={valgt ? 'hb-rad hb-rad-valgt' : 'hb-rad'} aria-current={valgt ? 'true' : undefined} data-testid={testid ?? `moete-rad-${m.id}`} style={{ '--hb-status': tokenVar(s.fg) } as CSSProperties}>
      <Ikon navn={s.ikon} className="hb-rad-ikon" />
      <span className="hb-rad-tittel">{moeteNavn(m)}</span>
      <span className="hb-rad-hoyre">
        <b className="hb-rad-fet">{m.itemCount}</b> {ord}
      </span>
      <span className="hb-rad-meta">
        <b className="hb-rad-status">{forMeg ? (s.etikettFor ?? s.etikett) : s.etikett}</b>
        {meta.filter(Boolean).map((l, n) => (
          <span key={n} className="hb-rad-ledd">
            <span aria-hidden="true" className="hb-prikk">
              ·
            </span>
            {l}
          </span>
        ))}
        {m.reasonsMissing ? (
          <span className="hb-rad-ledd hb-varsel">
            <span aria-hidden="true" className="hb-prikk">
              ·
            </span>
            <Ikon navn="warning-outline" />
            {m.reasonsMissing === 1 ? '1 begrunnelse mangler' : `${m.reasonsMissing} begrunnelser mangler`}
          </span>
        ) : null}
      </span>
    </Link>
  );
}

/**
 * Ett møte: agendaen mens det er planlagt, beslutningsloggen når det er sendt. Brukt i «Møter» og
 * i «Til deg» (W9), med samme innhold — så beslutningstakeren og den som registrerte ser det samme.
 */
export function MoeteDetalj({ id, tilbake, onKvittering }: { id: string; tilbake: { navn: string; til: string }; onKvittering?: (tekst: string) => void }) {
  const moete = useMoete(id);
  const config = useHypoteseConfig();
  const me = useMe();
  const tilgang = useTilgang();
  const smal = useSmal();
  const m = moete.data;

  const stripe = smal ? (
    <header className="h-stripe" data-testid="mo-stripe">
      <Link to={tilbake.til} className="panel-stripe-tilbake" aria-label={`Tilbake til ${tilbake.navn}`}>
        <Ikon navn="chevron-back-outline" />
      </Link>
      <span className="h-stripe-tittel">{m ? moeteNavn(m) : tilbake.navn}</span>
    </header>
  ) : null;

  if (moete.isPending) {
    return (
      <section className="hb-detalj" aria-label="Henter møtet">
        {stripe}
        <Skjelettrader etikett="Henter møtet" />
      </section>
    );
  }
  if (moete.isError || !m) {
    return (
      <section className="hb-detalj" aria-label="Fant ikke møtet">
        {stripe}
        <div className="hb-detalj-innhold">
          <h2 className="panel-h2">Fant ikke møtet</h2>
          <p className="hb-dempet">{moete.isError && erNettfeil(moete.error) ? 'Fikk ikke hentet møtet. Sjekk nettet og prøv igjen.' : 'Det kan være avlyst, eller ikke ditt å se.'}</p>
        </div>
      </section>
    );
  }
  return (
    <section className="hb-detalj mo-detalj" aria-labelledby="moete-h">
      {stripe}
      {m.status === 'planlagt' ? (
        <Agenda m={m} config={config.data} megId={me.data?.id} behandler={erHBehandler(tilgang)} />
      ) : (
        <Beslutningslogg m={m} config={config.data} megId={me.data?.id} leserPluss={erHLeserPluss(tilgang)} smal={smal} onKvittering={onKvittering} />
      )}
    </section>
  );
}

/**
 * Agendaen (W1): hypotesene i rekkefølge, hver med gripeflate (Alt ↑ / Alt ↓), meta-linjen,
 * anbefalingen som én signert setning og sitatene. En hypotese som har forlatt Vurderes står
 * gjennomstreket med grunnen og presenteres ikke. Foten er «Start møtet» for den som registrerer.
 */
function Agenda({ m, config, megId, behandler }: { m: DecisionMeeting; config: HypothesisConfig | undefined; megId: string | undefined; behandler: boolean }) {
  const naviger = useNavigate();
  const skriv = useMoeteSkriving(m.id);
  const registrar = m.registeredBy.id === megId;
  const rader = useMemo(() => alleRader(m), [m]);
  const aktive = rader.filter((i) => i.state !== 'tatt_ut');
  const detaljer = useHypoteser(aktive.map((i) => i.hypothesis.id));
  const ekstra = useMemo(() => Object.fromEntries(detaljer.flatMap((d) => (d.data ? [[d.data.id, d.data]] : []))), [detaljer]);
  const [leggTil, setLeggTil] = useState(false);
  const [endreMoete, setEndreMoete] = useState(false);
  const [avlyser, setAvlyser] = useState(false);
  const [feil, setFeil] = useState<string | null>(null);
  const [aapen, setAapen] = useState<{ id: string; hva: 'sitater' | 'anbefaling' } | null>(null);

  // L: «Legg til hypotese …» (W1). Aldri mens et felt har fokus.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const maal = e.target as HTMLElement | null;
      if (!behandler || (maal && /^(INPUT|TEXTAREA|SELECT)$/.test(maal.tagName)) || e.ctrlKey || e.metaKey || e.altKey || e.defaultPrevented) return;
      if (e.key === 'l' || e.key === 'L') {
        e.preventDefault();
        setLeggTil(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [behandler]);

  async function flytt(i: DecisionMeetingItem, steg: -1 | 1) {
    const ider = rader.map((x) => x.hypothesis.id);
    const fra = ider.indexOf(i.hypothesis.id);
    const til = fra + steg;
    if (til < 0 || til >= ider.length) return;
    [ider[fra], ider[til]] = [ider[til]!, ider[fra]!];
    setFeil(null);
    try {
      await skriv.mutateAsync({ handling: 'rekkefolge', hypoteseIder: ider });
    } catch (e) {
      setFeil(erApiFeil(e) ? e.brukertekst : 'Rekkefølgen ble ikke lagret. Prøv igjen.');
    }
  }

  async function start() {
    setFeil(null);
    // Vinduet åpnes i trykket, før kallet: nettleseren slipper det bare gjennom mens trykket er ferskt.
    aapneSkjermen(m.id);
    try {
      if (!m.startedAt) await skriv.mutateAsync({ handling: 'start' });
      void naviger(`/h/moter/${m.id}/kjor`);
    } catch (e) {
      setFeil(erApiFeil(e) ? (erNettfeil(e) ? 'Ikke startet — ingen nett. Prøv igjen når nettet er tilbake.' : e.brukertekst) : 'Noe gikk galt. Prøv igjen.');
    }
  }

  async function skrivRad(i: DecisionMeetingItem, body: { recommendation?: string | null; quoteSubmissionIds?: string[] }) {
    setFeil(null);
    try {
      await skriv.mutateAsync({ handling: 'endreRad', hypoteseId: i.hypothesis.id, body });
      setAapen(null);
    } catch (e) {
      setFeil(erApiFeil(e) ? (erNettfeil(e) ? 'Ikke lagret — ingen nett. Det du valgte står.' : e.brukertekst) : 'Noe gikk galt. Prøv igjen.');
    }
  }

  async function fjern(i: DecisionMeetingItem) {
    setFeil(null);
    try {
      await skriv.mutateAsync({ handling: 'fjernRad', hypoteseId: i.hypothesis.id });
    } catch (e) {
      setFeil(erApiFeil(e) ? e.brukertekst : 'Noe gikk galt. Prøv igjen.');
    }
  }

  const hvem = `${navnAv(m.decider)} bestemmer · ${registrar ? 'du registrerer utfallene på hans vegne' : `${navnAv(m.registeredBy)} registrerer`}`;
  const status = moeteStatus(m.status);

  return (
    <>
      <div className="hb-detalj-rull">
        <div className="hb-detalj-innhold ma">
          <div className="ml-crumb">
            <span>{`Møte · opprettet av ${m.registeredBy.id === megId ? 'deg' : navnAv(m.registeredBy)} ${kortDatoAv(m.createdAt)}`}</span>
            {registrar && !m.startedAt ? (
              <button type="button" className="panel-knapp-lenke" onClick={() => setEndreMoete(true)}>
                <Ikon navn="create-outline" />
                Endre dato eller beslutningstaker
              </button>
            ) : null}
          </div>
          <h2 id="moete-h" className="hb-tittel">
            {moeteNavn(m)}
          </h2>
          <p className="ml-naa">
            <span className="panel-pille panel-pille-outline" style={{ '--pille-fg': tokenVar(status.fg), '--pille-bg': tokenVar(status.bg) } as CSSProperties} data-status={m.status}>
              <Ikon navn={status.ikon} />
              {m.startedAt ? 'Møtet går' : status.etikett}
            </span>
            <span className="hb-dempet">{hvem}</span>
          </p>
          {feil ? <Varsel>{feil}</Varsel> : null}
          <section className="ma-agenda" aria-labelledby="ma-agenda-h">
            <div className="ma-agenda-topp">
              <h3 id="ma-agenda-h" className="hb-seksjon-tittel">{`Agenda · ${aktive.length} ${aktive.length === 1 ? 'hypotese' : 'hypoteser'}`}</h3>
              {behandler ? (
                <button type="button" className="panel-knapp-lenke" aria-keyshortcuts="L" title="Legg til hypotese (L)" onClick={() => setLeggTil(true)}>
                  <Ikon navn="add-outline" />
                  Legg til hypotese … <kbd aria-hidden="true">L</kbd>
                </button>
              ) : null}
            </div>
            {rader.length === 0 ? <p className="hb-dempet">Ingen hypoteser ennå. Legg til dem som skal opp — bare hypoteser i Vurderes kan stå her.</p> : null}
            <ol className="ma-rader">
              {rader.map((i) => (
                <AgendaRad
                  key={i.hypothesis.id}
                  i={i}
                  config={config}
                  megId={megId}
                  behandler={behandler}
                  evidens={ekstra[i.hypothesis.id]}
                  aapen={aapen?.id === i.hypothesis.id ? aapen.hva : null}
                  onAapne={(hva) => setAapen(hva ? { id: i.hypothesis.id, hva } : null)}
                  onFlytt={(steg) => void flytt(i, steg)}
                  onLagre={(body) => void skrivRad(i, body)}
                  onFjern={() => void fjern(i)}
                />
              ))}
            </ol>
          </section>
        </div>
      </div>
      {registrar && behandler ? (
        <Fot
          primarFoerst
          primar={{ label: m.startedAt ? 'Fortsett møtet' : 'Start møtet', ikon: 'tv-outline', onClick: () => void start(), opptatt: skriv.isPending, testid: 'ma-start' }}
          venstre={<span className="ml-fot-tekst">Skjermen i rommet åpnes i et eget vindu. Ingen innsender får beskjed i møtet.</span>}
          hoyre={
            m.startedAt ? null : (
              <button type="button" className="panel-knapp-lenke panel-knapp-lenke-fare" onClick={() => setAvlyser(true)}>
                Avlys møtet …
              </button>
            )
          }
        />
      ) : behandler ? (
        <div className="h-fot">
          <span className="ml-fot-tekst">{`${navnAv(m.registeredBy)} starter og kjører møtet.`}</span>
        </div>
      ) : null}
      {leggTil ? <LeggTilDialog m={m} config={config} onLukk={() => setLeggTil(false)} /> : null}
      {endreMoete ? <NyttMoeteDialog m={m} onLukk={() => setEndreMoete(false)} onOpprettet={() => setEndreMoete(false)} /> : null}
      {avlyser ? <AvlysDialog m={m} onLukk={() => setAvlyser(false)} /> : null}
    </>
  );
}

type Evidens = { evidenceCount: number; sourceStatus: string; supersedes?: { number: number } };

function AgendaRad({
  i,
  config,
  megId,
  behandler,
  evidens,
  aapen,
  onAapne,
  onFlytt,
  onLagre,
  onFjern,
}: {
  i: DecisionMeetingItem;
  config: HypothesisConfig | undefined;
  megId: string | undefined;
  behandler: boolean;
  evidens: Evidens | undefined;
  aapen: 'sitater' | 'anbefaling' | null;
  onAapne: (hva: 'sitater' | 'anbefaling' | null) => void;
  onFlytt: (steg: -1 | 1) => void;
  onLagre: (body: { recommendation?: string | null; quoteSubmissionIds?: string[] }) => void;
  onFjern: () => void;
}) {
  const h = i.hypothesis;
  const nr = nummerTekst(h.number);
  const tatt = i.state === 'tatt_ut';
  const o = hypoteseOppforing(hypoteseStatusNoekkel(tatt ? (i.removedStatus ?? h.status) : h.status, h.outcome));
  const kanSitere = i.quoteSubmissionIds !== undefined;
  const antall = evidens?.evidenceCount;

  const grip = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
    if (!e.altKey || (e.key !== 'ArrowUp' && e.key !== 'ArrowDown')) return;
    e.preventDefault();
    onFlytt(e.key === 'ArrowUp' ? -1 : 1);
  };

  const meta = [
    `${statusNavn(config, h) || 'Vurderes'}`,
    fristKort(h) ? `frist ${fristKort(h)}` : null,
    `fra ${navneliste(innsendere(h))}`,
    antall === undefined ? null : antall === 0 ? 'ingen innspill i grunnlaget' : `${antall} innspill i grunnlaget`,
  ].filter((x): x is string => x !== null);

  if (tatt) {
    const hvem = i.removedStatus === 'trukket' ? navnAv(h.submittedBy) : h.decider ? navnAv(h.decider) : null;
    return (
      <li className="ma-rad ma-rad-tatt" data-testid={`agenda-rad-${h.number}`}>
        <span className="ma-grip-plass" aria-hidden="true" />
        <Ikon navn={o.ikon} className="ma-rad-ikon" />
        <div className="ma-rad-kropp">
          <p className="ma-rad-tittel">
            <s>{h.title}</s> <span className="hb-dempet">{nr}</span>
          </p>
          <p className="ma-rad-linje hb-varsel">
            <Ikon navn="warning-outline" />
            {`${statusNavn(config, { status: i.removedStatus ?? h.status, outcome: h.outcome }) || 'Flyttet'} ${kortDatoAv(h.statusChangedAt)}${hvem ? ` av ${hvem}` : ''} — står ikke lenger i Vurderes og tas ut av agendaen`}
          </p>
          {behandler ? (
            <div className="ma-rad-handlinger">
              <button type="button" className="panel-knapp-lenke" onClick={onFjern}>
                Fjern fra agendaen
              </button>
            </div>
          ) : null}
        </div>
      </li>
    );
  }

  return (
    <li className="ma-rad" data-testid={`agenda-rad-${h.number}`} style={{ '--hb-status': tokenVar(o.fg) } as CSSProperties}>
      {behandler ? (
        <button type="button" className="ma-grip" aria-label={`Flytt ${nr} — Alt og pil opp eller ned`} aria-keyshortcuts="Alt+ArrowUp Alt+ArrowDown" title="Flytt (Alt ↑ / Alt ↓)" onKeyDown={grip}>
          <Ikon navn="reorder-two-outline" />
        </button>
      ) : (
        <span className="ma-grip-plass" aria-hidden="true" />
      )}
      <Ikon navn={o.ikon} className="ma-rad-ikon" />
      <div className="ma-rad-kropp">
        <p className="ma-rad-tittel">
          <b>{h.title}</b> <span className="hb-dempet">{nr}</span>
        </p>
        <p className="ma-rad-linje hb-dempet">
          {meta.join(' · ')}
          {evidens?.sourceStatus === 'maa_bygges' ? (
            <span className="ma-merke">
              {' · '}
              <Ikon navn="hammer-outline" />
              måling må bygges
            </span>
          ) : null}
          {evidens?.supersedes ? ` · bygger på ${nummerTekst(evidens.supersedes.number)}` : ''}
          {i.proposedOwner ? ` · foreslått eier ${navnAv(i.proposedOwner)}` : ''}
        </p>
        {aapen === 'anbefaling' ? (
          <Anbefaling start={i.recommendation?.text ?? ''} onLagre={(t) => onLagre({ recommendation: t.trim() ? t.trim() : null })} onAvbryt={() => onAapne(null)} />
        ) : i.recommendation ? (
          <p className="ma-anbefaling">
            <Ikon navn="document-text-outline" />
            <span>{`Anbefaling fra ${i.recommendation.by.id === megId ? 'deg' : navnAv(i.recommendation.by)}: ${i.recommendation.text}`}</span>
          </p>
        ) : null}
        <div className="ma-rad-handlinger">
          {antall === 0 ? (
            <span className="hb-dempet">Ingen innspill å sitere fra</span>
          ) : kanSitere && behandler ? (
            <button type="button" className="panel-knapp-lenke" aria-expanded={aapen === 'sitater'} onClick={() => onAapne(aapen === 'sitater' ? null : 'sitater')}>
              <Ikon navn="chatbox-ellipses-outline" />
              {`Sitater til skjermen · ${i.quoteCount ? `${i.quoteCount} av ${MAKS_SITATER} valgt` : 'ingen valgt'}`}
            </button>
          ) : i.quoteCount ? (
            <span className="hb-dempet">{i.quoteCount === 1 ? '1 sitat valgt' : `${i.quoteCount} sitater valgt`}</span>
          ) : null}
          {behandler && aapen !== 'anbefaling' ? (
            <button type="button" className="panel-knapp-lenke" onClick={() => onAapne('anbefaling')}>
              {i.recommendation ? (
                'Endre anbefalingen'
              ) : (
                <>
                  <Ikon navn="document-text-outline" />
                  Skriv anbefaling …
                </>
              )}
            </button>
          ) : null}
        </div>
        {aapen === 'sitater' ? <Sitatvelger i={i} onFerdig={(ider) => (ider ? onLagre({ quoteSubmissionIds: ider }) : onAapne(null))} /> : null}
      </div>
    </li>
  );
}

function Anbefaling({ start, onLagre, onAvbryt }: { start: string; onLagre: (tekst: string) => void; onAvbryt: () => void }) {
  const [tekst, setTekst] = useState(start);
  return (
    <div className="ma-anbefaling-skjema">
      <label className="panel-felt">
        <span>Anbefaling — én setning, signert med navnet ditt</span>
        <textarea rows={2} maxLength={500} value={tekst} onChange={(e) => setTekst(e.target.value)} />
      </label>
      <div className="panel-handlinger">
        <button type="button" className="panel-knapp-sekundaer" onClick={() => onLagre(tekst)}>
          Lagre anbefalingen
        </button>
        <Tekstknapp onClick={onAvbryt}>Avbryt</Tekstknapp>
      </div>
    </div>
  );
}

const SPOR: Record<string, string> = { feil: 'Feil', onske: 'Ønske' };
const PLATTFORM: Record<string, string> = { ios: 'iOS', android: 'Android', web: 'Web', tvos: 'tvOS', androidtv: 'Android TV', tizen: 'Tizen', webos: 'webOS' };

/**
 * Sitatvelgeren (W3): de lenkede sakene fra grunnlaget, høyst tre, ingen forhåndsvalgt. Ved tre
 * valgte er resten sperret med grunnen i foten. Rekkefølgen på veggen er rekkefølgen de velges i.
 * Bare for den som ser sakene — tjeneren gir sakene bare til en med rolle i Innspill, innenfor
 * sporene hennes (Kh7, M4.11).
 */
function Sitatvelger({ i, onFerdig }: { i: DecisionMeetingItem; onFerdig: (ider: string[] | null) => void }) {
  const grunnlag = useHypoteseGrunnlag(i.hypothesis.id);
  const [valgt, setValgt] = useState<string[]>(i.quoteSubmissionIds ?? []);
  const fullt = valgt.length >= MAKS_SITATER;
  const saker = grunnlag.data?.items ?? [];
  const rot = useRef<HTMLDivElement>(null);
  const endret = JSON.stringify(valgt) !== JSON.stringify(i.quoteSubmissionIds ?? []);

  return (
    <div
      ref={rot}
      role="group"
      aria-label="Sitater til skjermen"
      className="ma-sitater"
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onFerdig(null);
        }
      }}
    >
      {grunnlag.isPending ? (
        <Skjelettrader etikett="Henter sakene" />
      ) : grunnlag.isError ? (
        <Varsel tone="info">Sakene kunne ikke hentes. Valget står som før.</Varsel>
      ) : (
        <ul className="ma-sitat-liste">
          {saker.map(({ submission: s }) => {
            const paa = valgt.includes(s.id);
            const sperret = !paa && fullt;
            return (
              <li key={s.id}>
                <label className={sperret ? 'ma-sitat ma-sitat-sperret' : 'ma-sitat'}>
                  <input type="checkbox" checked={paa} disabled={sperret} aria-describedby={sperret ? `ma-sitat-grunn-${i.hypothesis.id}` : undefined} onChange={() => setValgt((v) => (paa ? v.filter((x) => x !== s.id) : [...v, s.id]))} />
                  <span className="ma-sitat-tekst">
                    <span>{s.text}</span>
                    <span className="hb-dempet ma-sitat-meta">{[SPOR[s.type] ?? s.type, `#${s.number}`, s.context?.platform ? (PLATTFORM[s.context.platform] ?? s.context.platform) : null, kortDatoAv(s.createdAt)].filter(Boolean).join(' · ')}</span>
                  </span>
                </label>
              </li>
            );
          })}
        </ul>
      )}
      <div className="ma-sitat-fot">
        <Ikon navn="information-circle-outline" />
        <span id={`ma-sitat-grunn-${i.hypothesis.id}`}>{`Vises på veggen for alle i møtet, ordrett og uten pseudonym.${fullt ? ' Tre er valgt — fjern én for å bytte.' : ''}`}</span>
        <button type="button" className="panel-knapp-lenke" onClick={() => onFerdig(endret ? valgt : null)}>
          Ferdig
        </button>
      </div>
    </div>
  );
}

/**
 * «Nytt møte» (W2): tre felt, én fylt knapp. Navnet er valgfritt og blir «Møte ‹dato›» (P2). Foten
 * sier regelen om innsender én gang, i stedet for en lås på hver rad senere. Samme dialog endrer
 * dato, beslutningstaker og navn mens møtet er planlagt («Endre dato eller beslutningstaker»).
 */
export function NyttMoeteDialog({ m, onLukk, onOpprettet }: { m?: DecisionMeeting; onLukk: () => void; onOpprettet: (m: DecisionMeeting) => void }) {
  const config = useHypoteseConfig();
  const opprett = useOpprettMoete();
  const skriv = useMoeteSkriving(m?.id ?? '');
  const deciders = config.data?.deciders ?? [];
  const [dato, setDato] = useState(m?.heldOn ?? isoDag(new Date()));
  const [hvem, setHvem] = useState(m?.decider.id ?? '');
  const [navn, setNavn] = useState(m?.title ?? '');
  const [feil, setFeil] = useState<string | null>(null);
  const valgtHvem = hvem || deciders[0]?.id || '';
  const besluttersNavn = navnAv(deciders.find((d) => d.id === valgtHvem));
  const grunn = !dato ? 'Velg en dato.' : !valgtHvem ? 'Det står ingen på beslutningstakerlista. Eier legger dem til i rommets Innstillinger.' : null;
  const opptatt = opprett.isPending || skriv.isPending;

  async function lagre() {
    if (grunn || opptatt) return;
    setFeil(null);
    try {
      if (m) {
        const ny = await skriv.mutateAsync({ handling: 'endre', body: { heldOn: dato, deciderId: valgtHvem, title: navn.trim() ? navn.trim() : null } });
        onOpprettet(ny);
      } else {
        const ny = await opprett.mutateAsync({ heldOn: dato, deciderId: valgtHvem, ...(navn.trim() ? { title: navn.trim() } : {}) });
        onOpprettet(ny);
      }
    } catch (e) {
      if (erApiFeil(e) && e.code === 'decider_is_submitter') setFeil(`${besluttersNavn} sendte inn en av hypotesene på agendaen og kan ikke avgjøre den.`);
      else setFeil(erApiFeil(e) ? (erNettfeil(e) ? 'Ikke lagret — ingen nett. Prøv igjen når nettet er tilbake.' : e.brukertekst) : 'Noe gikk galt. Prøv igjen.');
    }
  }

  return (
    <Dialog tittelId="mo-nytt-h" onLukk={onLukk} testid="mo-nytt">
      <form
        className="mo-nytt"
        onSubmit={(e) => {
          e.preventDefault();
          void lagre();
        }}
      >
        <h2 id="mo-nytt-h" className="panel-h2">
          {m ? 'Endre møtet' : 'Nytt møte'}
        </h2>
        <label className="panel-felt">
          <span>Dato</span>
          <input type="date" value={dato} min={m ? undefined : isoDag(new Date())} onChange={(e) => setDato(e.target.value)} required />
        </label>
        <label className="panel-felt">
          <span>Hvem bestemmer</span>
          <select value={valgtHvem} onChange={(e) => setHvem(e.target.value)}>
            {deciders.map((d) => (
              <option key={d.id} value={d.id}>
                {navnAv(d)}
              </option>
            ))}
          </select>
        </label>
        <label className="panel-felt">
          <span>
            Navn <span className="mu-hint">valgfritt</span>
          </span>
          <input type="text" maxLength={80} value={navn} placeholder={dato ? `Møte ${langDato(dato)}` : 'Møte'} onChange={(e) => setNavn(e.target.value)} />
        </label>
        <p className="hb-dempet">{m ? 'Hypotesene på agendaen står som de er.' : 'Hypotesene legges til etterpå, fra agendaen med L.'}</p>
        {feil ? <Varsel>{feil}</Varsel> : null}
        <div className="mo-nytt-fot">
          <span className="hb-dempet">{valgtHvem ? `${besluttersNavn} kan ikke være innsender av noe på agendaen.` : ''}</span>
          <span className="panel-handlinger">
            <Knapp label="Avbryt" kbd="Esc" lenke onClick={onLukk} />
            <span className="panel-knapp-med-grunn">
              <button type="submit" className="panel-knapp" aria-disabled={grunn ? true : undefined} aria-describedby={grunn ? 'mo-nytt-grunn' : undefined} aria-busy={opptatt || undefined}>
                {m ? 'Lagre' : 'Opprett møtet'}
              </button>
              {grunn ? (
                <span id="mo-nytt-grunn" className="h-grunn">
                  {grunn}
                </span>
              ) : null}
            </span>
          </span>
        </div>
      </form>
    </Dialog>
  );
}

/** «Legg til hypotese …» (W1, L): en hypotese i Vurderes, sist på agendaen, med anbefaling og foreslått eier om man vil. */
function LeggTilDialog({ m, config, onLukk }: { m: DecisionMeeting; config: HypothesisConfig | undefined; onLukk: () => void }) {
  const skriv = useMoeteSkriving(m.id);
  const vurderes = useHypoteseListe({ status: 'vurderes' });
  const paa = new Set(m.items.filter((i) => i.state !== 'tatt_ut').map((i) => i.hypothesis.id));
  const valg = (vurderes.data?.pages.flatMap((p) => p.items) ?? []).filter((h) => !paa.has(h.id));
  const [hyp, setHyp] = useState('');
  const [anbefaling, setAnbefaling] = useState('');
  const [eier, setEier] = useState('');
  const [feil, setFeil] = useState<string | null>(null);
  const grunn = !hyp ? 'Velg en hypotese.' : null;

  async function leggTil() {
    if (grunn || skriv.isPending) return;
    setFeil(null);
    try {
      await skriv.mutateAsync({ handling: 'leggTil', body: { hypothesisId: hyp, ...(anbefaling.trim() ? { recommendation: anbefaling.trim() } : {}), ...(eier ? { proposedOwnerId: eier } : {}) } });
      onLukk();
    } catch (e) {
      const h = valg.find((x) => x.id === hyp);
      if (erApiFeil(e) && e.code === 'decider_is_submitter') setFeil(`${navnAv(m.decider)} sendte inn ${nummerTekst(h?.number)} og kan ikke avgjøre den på sitt eget møte.`);
      else if (erApiFeil(e) && e.code === 'already_on_agenda') setFeil(`${nummerTekst(h?.number)} står alt på et møte som ikke er bekreftet.`);
      else setFeil(erApiFeil(e) ? e.brukertekst : 'Noe gikk galt. Prøv igjen.');
    }
  }

  return (
    <Dialog tittelId="mo-legg-h" onLukk={onLukk} testid="mo-legg">
      <h2 id="mo-legg-h" className="panel-h2">
        Legg til hypotese
      </h2>
      <label className="panel-felt">
        <span>Hypotese</span>
        <select value={hyp} onChange={(e) => setHyp(e.target.value)}>
          <option value="">{vurderes.isPending ? 'Henter …' : valg.length ? 'Velg en i Vurderes …' : 'Ingen i Vurderes står utenfor et møte'}</option>
          {valg.map((h) => (
            <option key={h.id} value={h.id}>
              {`${nummerTekst(h.number)} · ${h.title ?? ''}`}
            </option>
          ))}
        </select>
      </label>
      <label className="panel-felt">
        <span>
          Anbefaling <span className="mu-hint">valgfri, én setning</span>
        </span>
        <textarea rows={2} maxLength={500} value={anbefaling} onChange={(e) => setAnbefaling(e.target.value)} />
      </label>
      <label className="panel-felt">
        <span>
          Foreslått eier <span className="mu-hint">valgfri, binder ingenting</span>
        </span>
        <select value={eier} onChange={(e) => setEier(e.target.value)}>
          <option value="">Ingen</option>
          {(config?.members ?? [])
            .filter((p) => p.id !== m.decider.id)
            .map((p) => (
              <option key={p.id} value={p.id}>
                {navnAv(p)}
              </option>
            ))}
        </select>
      </label>
      {feil ? <Varsel>{feil}</Varsel> : null}
      <div className="panel-handlinger">
        <span className="panel-knapp-med-grunn">
          <button type="button" className="panel-knapp" aria-disabled={grunn ? true : undefined} aria-describedby={grunn ? 'mo-legg-grunn' : undefined} aria-busy={skriv.isPending || undefined} onClick={() => void leggTil()}>
            Legg til
          </button>
          {grunn ? (
            <span id="mo-legg-grunn" className="h-grunn">
              {grunn}
            </span>
          ) : null}
        </span>
        <Knapp label="Avbryt" kbd="Esc" lenke onClick={onLukk} />
      </div>
    </Dialog>
  );
}

/** «Avlys møtet …» (DD-53): møtet fjernes, hypotesene står i Vurderes med fristene sine. */
function AvlysDialog({ m, onLukk }: { m: DecisionMeeting; onLukk: () => void }) {
  const avlys = useAvlysMoete();
  const naviger = useNavigate();
  const [feil, setFeil] = useState<string | null>(null);
  async function ja() {
    setFeil(null);
    try {
      await avlys.mutateAsync(m.id);
      onLukk();
      void naviger(H_MOETER.sti, { replace: true, state: { kvittering: `${moeteNavn(m)} er avlyst. Hypotesene står i Vurderes med fristene sine.` } });
    } catch (e) {
      setFeil(erApiFeil(e) ? e.brukertekst : 'Noe gikk galt. Prøv igjen.');
    }
  }
  return (
    <Dialog tittelId="mo-avlys-h" onLukk={onLukk} testid="mo-avlys">
      <h2 id="mo-avlys-h" className="panel-h2">{`Avlyse ${moeteNavn(m)}?`}</h2>
      <p className="panel-tekst">{`Møtet fjernes. ${aktiveAntall(m)} hypoteser står i Vurderes med fristene sine, og kan legges på et annet møte.`}</p>
      {feil ? <Varsel>{feil}</Varsel> : null}
      <div className="panel-handlinger">
        <Knapp label="Avlys møtet" fare onClick={() => void ja()} />
        <Knapp label="Avbryt" kbd="Esc" lenke fokusFørst onClick={onLukk} />
      </div>
    </Dialog>
  );
}

const aktiveAntall = (m: DecisionMeeting) => m.items.filter((i) => i.state !== 'tatt_ut').length;

